(function () {
  // Set when the picker is closed without a choice, so a customer who declines is not
  // interrupted again. Cleared as soon as they drop back below the threshold.
  const DISMISSED_KEY = 'gwp:dismissed';

  // Holds the eligible subtotal the gift was last taken out of the cart by hand at.
  // The gift stays out while the cart still totals that much, and the gate offers it
  // again the moment the cart changes.
  const DECLINED_KEY = 'gwp:declined';

  let gateClaimed = false;

  // The variant the cart currently holds as its gift. The gate keeps it in step with
  // every cart update so the picker can seed its radios without a request of its own —
  // the picker lives outside the cart sections and is never re-rendered.
  let giftVariantId = 0;

  const readSession = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (error) {
      /* private mode */
      return null;
    }
  };

  const writeSession = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (error) {
      /* private mode */
    }
  };

  const clearSession = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      /* private mode */
    }
  };

  // The subtotal the gift was declined at, or null when it was not declined.
  const declinedAt = () => {
    const stored = readSession(DECLINED_KEY);
    return stored === null ? null : parseInt(stored, 10);
  };

  const cartSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  };

  const publishCart = (cart) => {
    if (!cart) return;
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  };

  const fetchCart = (sections) =>
    fetch(`${FoxTheme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (sections) cart.sections = sections;
        return cart;
      });

  const findGiftLine = (cart) =>
    (cart.items || []).find((item) => item.properties && item.properties._gwp === 'true');

  // What counts towards the threshold: everything but the gift, at its selling price
  // before any discount and without compare-at prices — what the gift-with-purchase
  // function in mlc-app checks, and what the reward bar draws.
  const eligibleSubtotal = (cart) =>
    (cart.items || [])
      .filter((item) => !(item.properties && item.properties._gwp === 'true'))
      .reduce((sum, item) => sum + item.original_line_price, 0);

  /**
   * Keeps the cart's gift line consistent with the threshold.
   *
   * Lives outside the cart sections, so it survives Section Rendering API re-renders
   * and stays subscribed for the whole page life. It seeds from Liquid-rendered state,
   * which means a cart that is already correct costs zero requests.
   */
  class GwpGate extends HTMLElement {
    connectedCallback() {
      // Only the first instance reconciles; a stray second mount stays passive so two
      // reconcilers can never fight over the same cart.
      if (gateClaimed) {
        this.passive = true;
        return;
      }
      gateClaimed = true;
      this.passive = false;

      this.mode = this.dataset.mode;
      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.variantIds = (this.dataset.variantIds || '')
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter(Boolean);

      if (!this.threshold || !this.variantIds.length) return;

      this.busy = false;
      this.dirty = false;
      this.chain = Promise.resolve();
      this.latestCart = null;

      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);

      this.onDocumentClick = this.onDocumentClick.bind(this);
      // Delegated: the reward bar's gift milestone and the gift's cart line are both
      // re-rendered with the cart, so neither can own a listener of its own.
      document.addEventListener('click', this.onDocumentClick);

      this.reconcileFromSeed();
    }

    disconnectedCallback() {
      if (this.passive) return;
      gateClaimed = false;
      if (this.unsubscribe) this.unsubscribe();
      document.removeEventListener('click', this.onDocumentClick);
    }

    get picker() {
      return document.querySelector('gwp-picker');
    }

    onDocumentClick(event) {
      // The removal itself is left to the cart's own remove control; all the gate does
      // is note that the gift went out by hand, before the cart update lands.
      if (event.target.closest('[data-gwp-remove]')) {
        this.decline();
        return;
      }

      const trigger = event.target.closest('[data-gwp-open]');
      // The reward bar disables its gift milestone until the gift is earned, and the
      // whole milestone is the button — so a click on its caption must respect that.
      if (!trigger || trigger.disabled) return;
      const picker = this.picker;
      if (!picker || typeof picker.show !== 'function') return;
      event.preventDefault();
      picker.show(trigger);
    }

    /**
     * Records that the gift was removed by hand, keyed to the subtotal it was removed
     * at. That key is what makes the decision expire: it holds for this cart, and any
     * change to the cart makes it stale, at which point the gate offers the gift again.
     */
    decline() {
      writeSession(DECLINED_KEY, String(this.eligible || 0));
    }

    reconcileFromSeed() {
      const lineKey = this.dataset.lineKey || '';
      const giftLine = lineKey
        ? {
            key: lineKey,
            quantity: parseInt(this.dataset.lineQty, 10) || 0,
            variant_id: parseInt(this.dataset.lineVariantId, 10) || 0,
          }
        : null;

      giftVariantId = giftLine ? giftLine.variant_id : 0;

      // No prompt on load: the threshold was crossed before this page existed, so a
      // popup would ambush someone who is just browsing. The reward bar's gift
      // milestone carries the invitation instead.
      this.apply(parseInt(this.dataset.eligibleSubtotal, 10) || 0, giftLine, false);
    }

    onCartUpdate(event) {
      const cart = event && event.cart;
      if (!cart || !cart.items) return;
      this.latestCart = cart;

      const giftLine = findGiftLine(cart);
      giftVariantId = giftLine ? giftLine.variant_id : 0;

      // A gift request is already in flight — collapse into one trailing reconcile.
      if (this.busy) {
        this.dirty = true;
        return;
      }
      this.apply(eligibleSubtotal(cart), giftLine);
    }

    /**
     * Compares the gift the cart should hold against the one it does, and returns a
     * single action. 'none' is what terminates the cartUpdate -> mutate -> cartUpdate
     * loop, so every branch that changes nothing must return it.
     */
    decide(eligible, giftLine) {
      const earned = eligible >= this.threshold;

      if (!earned) return giftLine ? { type: 'remove', key: giftLine.key } : { type: 'none' };

      if (giftLine) {
        // The chosen gift went out of stock or was dropped from the metafield.
        if (giftLine.variant_id && !this.variantIds.includes(giftLine.variant_id)) {
          return { type: 'remove', key: giftLine.key };
        }
        if (giftLine.quantity !== 1) return { type: 'fix', key: giftLine.key };
        return { type: 'none' };
      }

      if (this.mode === 'single') return { type: 'add', variantId: this.variantIds[0] };
      return { type: 'prompt' };
    }

    apply(eligible, giftLine, allowPrompt = true) {
      // Kept for `decline`, which fires on a click and so has no cart of its own to read.
      this.eligible = eligible;

      if (eligible < this.threshold) {
        // Crossing the threshold again should prompt again.
        clearSession(DISMISSED_KEY);
        clearSession(DECLINED_KEY);
      } else if (!giftLine && declinedAt() !== null && declinedAt() !== eligible) {
        // The cart has moved on since the gift was removed by hand, so the decision no
        // longer applies to what is in front of the customer — run the gift logic from
        // scratch, prompt included.
        clearSession(DECLINED_KEY);
        clearSession(DISMISSED_KEY);
      }

      const action = this.decide(eligible, giftLine);

      if (action.type === 'none') return;

      // Still the very cart the gift was removed from — leave it out.
      if ((action.type === 'add' || action.type === 'prompt') && declinedAt() === eligible) return;

      if (action.type === 'prompt') {
        if (allowPrompt) this.prompt();
        return;
      }

      this.run(action);
    }

    prompt() {
      const picker = this.picker;
      if (!picker || typeof picker.show !== 'function' || picker.open) return;

      if (readSession(DISMISSED_KEY) === '1') return;

      picker.show();
    }

    run(action) {
      this.busy = true;
      this.dirty = false;
      this.chain = this.chain
        .then(() => this.mutate(action))
        .then(publishCart)
        .catch((error) => console.error('[GWP]', error))
        .finally(() => {
          this.busy = false;
          if (this.dirty && this.latestCart) {
            this.apply(eligibleSubtotal(this.latestCart), findGiftLine(this.latestCart));
          }
        });
    }

    mutate(action) {
      if (action.type === 'add') return GwpGate.addGift(action.variantId);
      return GwpGate.changeGift(action.key, action.type === 'remove' ? 0 : 1);
    }

    static addGift(variantId) {
      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1, properties: { _gwp: 'true' } }],
        sections: cartSections(),
        sections_url: window.location.pathname,
      });

      return fetch(`${FoxTheme.routes.cart_add_url}`, { ...FoxTheme.utils.fetchConfig(), body })
        .then((response) => response.json())
        .then((parsed) => {
          // 422: sold out between render and click. Abort rather than retry.
          if (parsed.status) {
            console.warn('[GWP] gift could not be added:', parsed.description || parsed.message);
            return null;
          }
          // /cart/add.js returns only the added items, so re-read the full cart and
          // carry the rendered sections over from the add response.
          return fetchCart(parsed.sections);
        });
    }

    static changeGift(key, quantity) {
      const body = JSON.stringify({
        id: key,
        quantity,
        sections: cartSections(),
        sections_url: window.location.pathname,
      });

      return fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), body })
        .then((response) => response.json())
        .then((parsed) => (parsed.status ? null : parsed));
    }
  }

  /**
   * The gift chooser. Adds the selected gift, swapping out a previous choice first so
   * the cart never holds two gifts.
   */
  class GwpPicker extends ModalComponent {
    get isLockingNeeded() {
      return true;
    }

    get requiresBodyAppended() {
      return true;
    }

    get focusElement() {
      return this.querySelector('.gwp-option__input:checked') || this.querySelector('.gwp-option__input') || this;
    }

    connectedCallback() {
      super.connectedCallback();

      this.confirmButton = this.querySelector('[data-gwp-confirm]');
      this.errorMessage = this.querySelector('[data-gwp-error]');
      this.inputs = Array.from(this.querySelectorAll('.gwp-option__input'));
      this.claimed = false;

      this.confirmButton.addEventListener('click', () => this.confirm(), { signal: this.abortController.signal });
      this.inputs.forEach((input) =>
        input.addEventListener('change', () => this.hideError(), { signal: this.abortController.signal })
      );
    }

    prepareToShow() {
      this.claimed = false;
      this.hideError();
      this.syncSelection();
    }

    /**
     * The picker sits outside the cart sections, so its radios still carry whatever the
     * page was rendered with. Re-seed them from the gift the cart actually holds — once
     * the gift has been removed by hand that means nothing is selected.
     */
    syncSelection() {
      this.inputs.forEach((input) => {
        input.checked = giftVariantId > 0 && parseInt(input.value, 10) === giftVariantId;
      });
    }

    handleAfterHide() {
      super.handleAfterHide();
      if (this.claimed) return;
      writeSession(DISMISSED_KEY, '1');
    }

    get selectedVariantId() {
      const checked = this.inputs.find((input) => input.checked);
      return checked ? parseInt(checked.value, 10) : 0;
    }

    hideError() {
      this.errorMessage.hidden = true;
    }

    showError(message) {
      this.errorMessage.textContent = message;
      this.errorMessage.hidden = false;
    }

    setLoading(loading) {
      this.confirmButton.classList.toggle('btn--loading', loading);
      this.confirmButton.disabled = loading;
    }

    confirm() {
      const variantId = this.selectedVariantId;
      if (!variantId) {
        this.showError(window.FoxTheme.gwpStrings.chooseOne);
        return;
      }

      this.setLoading(true);

      // Read the live cart rather than trusting seeded markup: the picker can be
      // reopened long after the page rendered, and line keys change as the cart does.
      fetchCart()
        .then((cart) => {
          const current = findGiftLine(cart);
          if (!current) return true;
          if (current.variant_id === variantId) return null;
          // Drop the previous gift before adding the new one, so a failed swap can
          // never leave two gift lines behind.
          return GwpGate.changeGift(current.key, 0).then((updated) => {
            if (!updated) throw new Error('could not remove the previous gift');
            return true;
          });
        })
        .then((shouldAdd) => {
          // Whatever was decided before, the gift is wanted now.
          clearSession(DECLINED_KEY);

          // Already holding exactly this gift — nothing to do but close.
          if (shouldAdd === null) {
            this.claimed = true;
            this.hide();
            return null;
          }
          return GwpGate.addGift(variantId);
        })
        .then((cart) => {
          if (this.claimed) return;
          if (!cart) {
            this.showError(window.FoxTheme.gwpStrings.unavailable);
            return;
          }
          this.claimed = true;
          publishCart(cart);
          this.hide();
        })
        .catch((error) => {
          console.error('[GWP]', error);
          this.showError(window.FoxTheme.gwpStrings.unavailable);
        })
        .finally(() => this.setLoading(false));
    }
  }

  // The picker is defined first: defining an element upgrades its instances
  // synchronously, and the gate reaches for the picker as soon as it connects.
  customElements.define('gwp-picker', GwpPicker);
  customElements.define('gwp-gate', GwpGate);
})();
